import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, TrendingUp, DollarSign, Calendar, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const EntreesTab = () => {
  const [entrees, setEntrees] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedEntree, setSelectedEntree] = useState(null);
  const [filterCaisse, setFilterCaisse] = useState('');

  const [formData, setFormData] = useState({
    nature: 'ENTREE',
    type_mouvement: 'VERSEMENT',
    montant: '',
    description: '',
    caisse: '',
    utilisateur: ''
  });

  const typeOptions = [
    { value: 'VERSEMENT', label: 'Versement étudiant' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  useEffect(() => {
    fetchEntrees();
    fetchCaisses();
  }, []);

  const fetchEntrees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/finance/mouvements/');
      // Vérifier que la réponse contient des données
      const data = response.data || [];
      const entreesData = Array.isArray(data) 
        ? data.filter(mouvement => mouvement.nature === 'ENTREE')
        : [];
      setEntrees(entreesData);
    } catch (error) {
      console.error('Erreur lors du chargement des entrées:', error);
      toast.error('Erreur lors du chargement des entrées');
      setEntrees([]); // S'assurer qu'on a un tableau vide en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  const fetchCaisses = async () => {
    try {
      const response = await api.get('/finance/caisses/');
      setCaisses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des caisses:', error);
      toast.error('Erreur lors du chargement des caisses');
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        utilisateur: 1 // TODO: Récupérer l'utilisateur connecté
      };

      if (modalMode === 'add') {
        await api.post('/finance/mouvements/', dataToSubmit);
        toast.success('Entrée ajoutée avec succès');
      } else {
        await api.put(`/finance/mouvements/${selectedEntree.id}/`, dataToSubmit);
        toast.success('Entrée modifiée avec succès');
      }

      fetchEntrees();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de l\'entrée');
    }
  };

  const handleEdit = (entree) => {
    setSelectedEntree(entree);
    setFormData({
      nature: entree.nature,
      type_mouvement: entree.type_mouvement,
      montant: entree.montant,
      description: entree.description || '',
      caisse: entree.caisse_id,
      utilisateur: entree.utilisateur_id
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?')) {
      try {
        await api.delete(`/finance/mouvements/${id}/`);
        fetchEntrees();
        toast.success('Entrée supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de l\'entrée');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nature: 'ENTREE',
      type_mouvement: 'VERSEMENT',
      montant: '',
      description: '',
      caisse: '',
      utilisateur: ''
    });
    setSelectedEntree(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrage des données
  const filteredEntrees = entrees.filter(entree => {
    const matchSearch = entree.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       entree.type_mouvement_display?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCaisse = !filterCaisse || entree.caisse_id.toString() === filterCaisse;
    return matchSearch && matchCaisse;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEntrees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntrees.length / itemsPerPage);

  const caisseOptions = caisses.map(caisse => ({
    value: caisse.id,
    label: `Caisse ${caisse.annee_academique_libelle || 'Non définie'} - ${formatCurrency(caisse.solde)}`
  }));

  // Calculer les statistiques
  const totalEntrees = entrees.reduce((sum, entree) => sum + (entree.montant || 0), 0);
  const nombreEntrees = entrees.length;
  const moyenneEntrees = nombreEntrees > 0 ? totalEntrees / nombreEntrees : 0;

  // Calculer les entrées du mois en cours
  const currentMonth = new Date();
  const entreesDuMois = entrees.filter(entree => {
    const entreeDate = new Date(entree.date_mouvement);
    return entreeDate.getMonth() === currentMonth.getMonth() && 
           entreeDate.getFullYear() === currentMonth.getFullYear();
  });
  const totalMoisCourant = entreesDuMois.reduce((sum, entree) => sum + (entree.montant || 0), 0);

  // Préparer les données pour les graphiques
  const entreesByType = entrees.reduce((acc, entree) => {
    const type = entree.type_mouvement_display || entree.type_mouvement;
    acc[type] = (acc[type] || 0) + entree.montant;
    return acc;
  }, {});

  const pieData = Object.entries(entreesByType).map(([type, montant]) => ({
    name: type,
    value: montant,
    percentage: entrees.length > 0 ? ((montant / totalEntrees) * 100).toFixed(1) : 0
  }));

  // Entrées par mois (derniers 6 mois) - amélioration de la logique
  const now = new Date();
  const monthsToShow = 6;
  const monthsData = {};

  // Générer les 6 derniers mois
  for (let i = monthsToShow - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    monthsData[monthKey] = {
      mois: monthLabel,
      montant: 0
    };
  }

  // Ajouter les données réelles
  entrees.forEach(entree => {
    const date = new Date(entree.date_mouvement);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (monthsData[monthKey]) {
      monthsData[monthKey].montant += entree.montant;
    }
  });

  const lineData = Object.values(monthsData);

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow border border-green-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500 mr-4">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-green-800">Total Entrées</h3>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(totalEntrees)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500 mr-4">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Nombre d'Entrées</h3>
              <p className="text-2xl font-bold text-blue-900">{nombreEntrees}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-500 mr-4">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-purple-800">Moyenne par Entrée</h3>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(moyenneEntrees)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg shadow border border-orange-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-500 mr-4">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-orange-800">Ce Mois-ci</h3>
              <p className="text-2xl font-bold text-orange-900">{formatCurrency(totalMoisCourant)}</p>
              <p className="text-xs text-orange-600 mt-1">{entreesDuMois.length} entrée(s)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par type */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Type</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({percent}) => `${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [formatCurrency(value), name]}
                  labelFormatter={(label) => `Type: ${label}`}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune entrée enregistrée</p>
            </div>
          )}
        </div>

        {/* Évolution mensuelle */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution Mensuelle</h3>
          {lineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={lineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="mois" 
                  tick={{ fontSize: 12 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Montant']}
                  labelFormatter={(label) => `Mois: ${label}`}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="montant" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune donnée disponible pour cette période</p>
            </div>
          )}
        </div>
      </div>

      {/* Header avec bouton d'ajout et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une entrée..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterCaisse}
              onChange={(e) => setFilterCaisse(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Toutes les caisses</option>
              {caisses.map(caisse => (
                <option key={caisse.id} value={caisse.id}>
                  {caisse.annee_academique_libelle || 'Non définie'}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Entrée
        </button>
      </div>

      {/* Tableau des entrées */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    Aucune entrée trouvée
                  </td>
                </tr>
              ) : (
                currentItems.map((entree) => (
                  <tr key={entree.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(entree.date_mouvement)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entree.type_mouvement_display}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      +{formatCurrency(entree.montant)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {entree.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(entree)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entree.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvelle Entrée' : 'Modifier l\'Entrée'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Type de mouvement"
            type="react-select"
            options={typeOptions}
            value={formData.type_mouvement}
            onChange={(value) => handleInputChange('type_mouvement', value)}
            required
          />

          <FormInput
            label="Montant (FCFA)"
            type="number"
            value={formData.montant}
            onChange={(e) => handleInputChange('montant', e.target.value)}
            min="0"
            required
          />

          <FormInput
            label="Caisse"
            type="react-select"
            options={caisseOptions}
            value={formData.caisse}
            onChange={(value) => handleInputChange('caisse', value)}
            required
          />

          <FormInput
            label="Description"
            type="textarea"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EntreesTab; 